import axios from "axios";
import React from "react";

import { classNames } from "../../../helpers";
import { getUrlForCronServer } from "../../../utils/confighelpers";

/** Shared bits for the Account settings tabs (new design). */

/** A white card with a heading, used for every section. */
export function SettingsCard({ title, subtitle, children, footer = null, aside = null, className = "" }) {
  return (
    <section className={classNames("rounded-3xl border border-gray-200 bg-white p-5 shadow-[0_1px_2px_rgba(29,19,32,0.04)] sm:p-7", className)}>
      {(title || aside) && (
        <div className="flex items-start gap-4">
          <div className="min-w-0 flex-1">
            {title && <h2 className="text-lg font-black tracking-tight text-gray-900">{title}</h2>}
            {subtitle && <p className="mt-1 text-sm text-gray-600">{subtitle}</p>}
          </div>
          {aside}
        </div>
      )}
      {children}
      {footer && <div className="mt-6 flex flex-wrap items-center justify-end gap-3 border-t border-gray-100 pt-5">{footer}</div>}
    </section>
  );
}

/** Primary / secondary buttons in the new theme. */
export function PrimaryButton({ children, busy = false, disabled = false, onClick, type = "button", className = "" }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || busy}
      className={classNames(
        "inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-brown-coffee px-5 text-sm font-extrabold text-white hover:bg-light-brown-coffee disabled:cursor-not-allowed disabled:bg-gray-300",
        className
      )}
    >
      {busy && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
      {children}
    </button>
  );
}

export function SecondaryButton({ children, busy = false, disabled = false, onClick, className = "" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || busy}
      className={classNames(
        "inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-2xl border border-gray-200 bg-white px-4 text-sm font-extrabold text-gray-900 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60",
        className
      )}
    >
      {busy && <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-200 border-t-gray-700" />}
      {children}
    </button>
  );
}

/** On/off switch. */
export function Toggle({ checked, onChange, disabled = false, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={!!checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={classNames(
        "flex h-7 w-12 shrink-0 items-center rounded-full p-[3px] transition-colors disabled:cursor-not-allowed disabled:opacity-50",
        checked ? "justify-end bg-indigo-bz" : "justify-start bg-[#D7CFDA]"
      )}
    >
      <span className="h-[22px] w-[22px] rounded-full bg-white shadow" />
    </button>
  );
}

/** Read-only value row (label + value), e.g. login details. */
export function ReadOnlyField({ label, value, hint, icon }) {
  return (
    <div className="flex items-center gap-4 border-t border-gray-100 py-4 first:border-t-0">
      {icon && <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#F3E9FF] text-indigo-bz">{icon}</span>}
      <div className="min-w-0 flex-1">
        <div className="text-[15px] font-extrabold text-gray-900">{label}</div>
        <div className="truncate text-sm text-gray-600">{value || "–"}</div>
        {hint && <div className="mt-0.5 text-xs text-gray-500">{hint}</div>}
      </div>
    </div>
  );
}

/** Same upload the old TwDropzone used: POST {cron}media-upload → { url }. */
export async function uploadImage(file) {
  const data = new FormData();
  data.append("uploadingFor", "TestClass");
  data.append("file", file);
  const res = await axios.request({
    method: "post",
    maxBodyLength: Infinity,
    url: `${getUrlForCronServer()}media-upload`,
    headers: { "Content-Type": "multipart/form-data" },
    data,
  });
  return res?.data?.url || "";
}

const MAX_BYTES = 1024 * 1024;
const ACCEPT = ["image/jpeg", "image/png", "image/gif", "image/webp"];

/**
 * Photo/logo picker: shows the image (or initials), a "Change" button and uploads on pick.
 * onUploaded(url) is called with the uploaded URL.
 */
export function ImagePicker({ value, fallback, rounded = "full", buttonLabel = "Upload photo", onUploaded, onError, help = "JPG, PNG, GIF or WebP, up to 1 MB." }) {
  const inputRef = React.useRef(null);
  const [busy, setBusy] = React.useState(false);
  const [broken, setBroken] = React.useState(false);
  React.useEffect(() => setBroken(false), [value]);

  const pick = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (!ACCEPT.includes(file.type)) return onError && onError("Please choose a JPG, PNG, GIF or WebP image.");
    if (file.size > MAX_BYTES) return onError && onError("That image is bigger than 1 MB. Please choose a smaller one.");
    setBusy(true);
    try {
      const url = await uploadImage(file);
      if (!url) throw new Error("no url");
      await onUploaded(url);
    } catch (err) {
      onError && onError("Couldn't upload the image. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const shape = rounded === "full" ? "rounded-full" : "rounded-2xl";
  return (
    <div className="flex items-center gap-4 sm:gap-5">
      <span className={classNames("relative flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden bg-[#EDE5F0]", shape)}>
        {value && !broken ? (
          <img src={value} alt="" onError={() => setBroken(true)} className={classNames("h-full w-full", rounded === "full" ? "object-cover" : "bg-white object-contain")} />
        ) : (
          fallback
        )}
        {busy && (
          <span className="absolute inset-0 flex items-center justify-center bg-white/70">
            <span className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-brown-coffee" />
          </span>
        )}
      </span>
      <div>
        <SecondaryButton busy={busy} onClick={() => inputRef.current && inputRef.current.click()}>
          {buttonLabel}
        </SecondaryButton>
        <p className="mt-1.5 text-xs text-gray-500">{help}</p>
        <input ref={inputRef} type="file" accept={ACCEPT.join(",")} className="hidden" onChange={pick} />
      </div>
    </div>
  );
}

/** Initials for a name ("Studio Noord" → "SN"). */
export const initialsOf = (name, fallback = "?") =>
  String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || fallback;
